import React, { useEffect, useState } from 'react';
import { BriefcaseBusiness, Loader2, UserRoundCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '../components/layout/MainLayout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { DIENSTGRAD_CONFIG } from '../lib/dienstgrade';
import { kategorienApi } from '../lib/api';

const dienstgradLabel = (dienstgrad) => (
  DIENSTGRAD_CONFIG[(dienstgrad || '').toUpperCase()]?.label
  || dienstgrad
  || 'Kein Dienstgrad hinterlegt'
);

export default function SachgebietePage() {
  const [sachgebiete, setSachgebiete] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    kategorienApi.getSachgebiete()
      .then((response) => setSachgebiete(response.data?.sachgebiete || []))
      .catch((error) => {
        toast.error(error?.response?.data?.detail || 'Sachgebiete konnten nicht geladen werden');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <MainLayout>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BriefcaseBusiness className="w-5 h-5" />
              Sachgebiete
            </CardTitle>
            <CardDescription>
              Übersicht der in den Benutzergruppen freigegebenen Sachgebiete und ihrer aktiven Mitarbeiter.
            </CardDescription>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          </div>
        ) : sachgebiete.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Noch keine Benutzergruppe ist als Sachgebiet freigegeben.
            </CardContent>
          </Card>
        ) : (
          <div
            className="grid gap-4 items-start overflow-x-auto pb-2"
            style={{ gridTemplateColumns: `repeat(${sachgebiete.length}, minmax(260px, 1fr))` }}
          >
            {sachgebiete.map((sachgebiet) => {
              const weitereMitarbeiter = sachgebiet.mitarbeiter.filter(
                (mitarbeiter) => mitarbeiter.id !== sachgebiet.hauptverantwortlicher?.id
              );
              return (
              <section key={sachgebiet.id} className="min-w-[260px] rounded-xl border bg-card overflow-hidden">
                <header
                  className="border-b p-4"
                  style={{ borderTop: `5px solid ${sachgebiet.farbe || '#64748b'}` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-semibold text-lg">{sachgebiet.name}</h2>
                    <Badge variant="secondary">
                      {sachgebiet.mitarbeiter.length}
                    </Badge>
                  </div>
                  {sachgebiet.beschreibung && (
                    <p className="mt-1 text-xs text-muted-foreground">{sachgebiet.beschreibung}</p>
                  )}
                </header>

                <div className="p-3 space-y-2">
                  {sachgebiet.hauptverantwortlicher ? (
                    <Card className="border-primary/40 bg-primary/5 shadow-none">
                      <CardContent className="p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <UserRoundCheck className="w-4 h-4 text-primary" />
                          <Badge>Hauptverantwortlich</Badge>
                        </div>
                        <p className="font-semibold">
                          {sachgebiet.hauptverantwortlicher.nachname},{' '}
                          {sachgebiet.hauptverantwortlicher.vorname}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {dienstgradLabel(sachgebiet.hauptverantwortlicher.dienstgrad)}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="flex items-center gap-2 rounded-lg border border-dashed border-amber-500/50 p-3 text-sm text-amber-700 dark:text-amber-400">
                      <UserRoundCheck className="w-4 h-4" />
                      Noch kein Hauptverantwortlicher festgelegt
                    </div>
                  )}

                  {weitereMitarbeiter.length === 0 ? (
                    <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      Keine weiteren Mitarbeiter zugeordnet
                    </div>
                  ) : weitereMitarbeiter.map((mitarbeiter) => (
                    <Card key={mitarbeiter.id} className="shadow-none">
                      <CardContent className="p-3">
                        <p className="font-medium">
                          {mitarbeiter.nachname}, {mitarbeiter.vorname}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {dienstgradLabel(mitarbeiter.dienstgrad)}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
